// src/pages/CreateArticlePage/CreateArticlePage.tsx

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useArticles } from '../../hooks/useArticles'
import * as articleService from '../../services/articleService'
import styles from './CreateArticlePage.module.css'
import { AVAILABLE_CATEGORIES, AVAILABLE_TAGS } from '../../constants/articleOptions'

// Import the markdown editor
import MDEditor from '@uiw/react-md-editor'
import '@uiw/react-md-editor/markdown-editor.css'

/* ─────────────────────────────────────────────────
   Icônes SVG inline
───────────────────────────────────────────────── */

const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.backIcon}>
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
)

export function CreateArticlePage() {
  const { token, user } = useAuth()
  const navigate = useNavigate()
  const { refetch } = useArticles()

  const [colorMode, setColorMode] = useState<'light' | 'dark'>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  )

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setColorMode(document.documentElement.classList.contains('dark') ? 'dark' : 'light')
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [content, setContent] = useState('')
  const [cover, setCover] = useState<File | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isAdmin = user?.roles?.includes('Admin')

  // Redirect if not admin
  if (!token || !isAdmin) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied}>
          <h1>Accès refusé</h1>
          <p>Vous devez être administrateur pour créer un article.</p>
          <Link to="/blog" className={styles.backLink}>Retour au blog</Link>
        </div>
      </div>
    )
  }

  const handleCategoryChange = (category: string, checked: boolean) => {
    if (checked) {
      if (selectedCategories.length >= 5) {
        setError('Vous ne pouvez sélectionner que maximum 5 catégories.')
        return
      }
      setSelectedCategories(prev => [...prev, category])
    } else {
      setSelectedCategories(prev => prev.filter(c => c !== category))
    }
    setError(null)
  }

  const handleTagChange = (tag: string, checked: boolean) => {
    if (checked) {
      if (selectedTags.length >= 5) {
        setError('Vous ne pouvez sélectionner que maximum 5 tags.')
        return
      }
      setSelectedTags(prev => [...prev, tag])
    } else {
      setSelectedTags(prev => prev.filter(t => t !== tag))
    }
    setError(null)
  }

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (file) {
      const allowedExtensions = ['.jpeg', '.jpg', '.png', '.webp', '.svg']
      const extension = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!allowedExtensions.includes(extension)) {
        setError('Extension de fichier non autorisée. Utilisez .jpeg, .jpg, .png, .webp ou .svg')
        return
      }
      if (file.size > 1 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas excéder 1MB.')
        return
      }
    }
    setCover(file)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!title.trim() || !description.trim() || !content.trim()) {
      setError('Titre, description et contenu sont requis.')
      return
    }

    try {
      setLoading(true)
      console.log('Création d\'article en cours...')

      const payload: articleService.NewArticlePayload = {
        title: title.trim(),
        description: description.trim(),
        content: content.trim(),
        cover: cover || undefined,
        categories: selectedCategories,
        tags: selectedTags,
      }

      const article = await articleService.createArticle(token, payload)
      console.log('Article créé avec succès:', article)

      refetch()
      navigate('/blog')
    } catch (err: any) {
      // 500 = bug de sérialisation côté API (référence circulaire Newtonsoft.Json)
      // L'article EST créé en base — on redirige quand même vers le blog.
      if (err?.status === 500) {
        refetch()
        navigate('/blog')
        return
      }
      console.error('Erreur lors de la création:', err)
      setError(err.message || 'Erreur lors de la création de l\'article.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/blog')
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb} aria-label="Fil d'Ariane">
          <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
          <span className={styles.breadcrumbSep} aria-hidden="true">/</span>
          <Link to="/blog" className={styles.breadcrumbLink}>Blog</Link>
          <span className={styles.breadcrumbSep} aria-hidden="true">/</span>
          <span className={styles.breadcrumbCurrent}>Nouvel article</span>
        </nav>

        <Link to="/blog" className={styles.back}>
          <IconArrowLeft />
          Retour au blog
        </Link>

        <div className={styles.container}>
          <h1 className={styles.title}>Créer un nouvel article</h1>

          <form className={styles.form} onSubmit={handleSubmit}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.field}>
              <label htmlFor="title" className={styles.label}>
                Titre *
              </label>
              <input
                id="title"
                type="text"
                className={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="description" className={styles.label}>
                Description *
              </label>
              <textarea
                id="description"
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                rows={3}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="cover" className={styles.label}>
                Image de couverture
              </label>
              <input
                id="cover"
                type="file"
                accept=".jpeg,.jpg,.png,.webp,.svg"
                onChange={handleCoverChange}
                disabled={loading}
                className={styles.fileInput}
              />
              <small className={styles.hint}>
                Extensions autorisées: .jpeg, .jpg, .png, .webp, .svg (max 1MB)
              </small>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                Catégories (max 5)
                {selectedCategories.length > 0 && (
                  <span className={styles.selectedCount}> ({selectedCategories.length} sélectionnée{selectedCategories.length > 1 ? 's' : ''})</span>
                )}
              </label>
              <div className={styles.checkboxGroup}>
                {AVAILABLE_CATEGORIES.map(category => (
                  <label key={category} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={(e) => handleCategoryChange(category, e.target.checked)}
                      disabled={loading}
                    />
                    {category}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                Tags (max 5)
                {selectedTags.length > 0 && (
                  <span className={styles.selectedCount}> ({selectedTags.length} sélectionné{selectedTags.length > 1 ? 's' : ''})</span>
                )}
              </label>
              <div className={styles.checkboxGroup}>
                {AVAILABLE_TAGS.map(tag => (
                  <label key={tag} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={(e) => handleTagChange(tag, e.target.checked)}
                      disabled={loading}
                    />
                    {tag}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                Contenu (Markdown) *
              </label>
              <div data-color-mode={colorMode}>
                <MDEditor
                  value={content}
                  onChange={(val) => setContent(val || '')}
                  preview="edit"
                  hideToolbar={false}
                  visibleDragbar={false}
                  textareaProps={{
                    placeholder: 'Écrivez votre article en Markdown...',
                  }}
                />
              </div>
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                onClick={handleCancel}
                className={styles.cancelBtn}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
              >
                {loading ? 'Création...' : 'Créer l\'article'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}